import React from "react";
import Sheet from "@mui/joy/Sheet";
import Step from "@mui/joy/Step";
import StepIndicator from "@mui/joy/StepIndicator";
import Stepper from "@mui/joy/Stepper";
import Typography from "@mui/joy/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

/**
 * Opt-in static stepper for a TIMELINE element: a configured `steps` list with a `current_step`
 * renders a vertical stepper (done / current / upcoming) and needs no data source. Without `steps`
 * the timeline is unchanged.
 */
export interface StepperStep {
  label: string;
  description?: string;
}

export interface StaticStepperConfig {
  steps: StepperStep[];
  /** Label of the current step. Steps before it are done, after it upcoming. */
  currentStep?: string;
}

/** Read `steps` and `current_step` from the element props or its `timeline_config`. */
export function readStaticStepper(
  props: Record<string, unknown>,
): StaticStepperConfig | undefined {
  const nested =
    props.timeline_config && typeof props.timeline_config === "object"
      ? (props.timeline_config as Record<string, unknown>)
      : {};
  const rawSteps = nested.steps ?? props.steps;
  if (!Array.isArray(rawSteps)) return undefined;
  const steps: StepperStep[] = [];
  for (const raw of rawSteps) {
    if (typeof raw === "string" && raw) steps.push({ label: raw });
    else if (raw && typeof raw === "object") {
      const { label, description } = raw as Record<string, unknown>;
      if (typeof label === "string" && label)
        steps.push({
          label,
          description:
            typeof description === "string" && description
              ? description
              : undefined,
        });
    }
  }
  if (steps.length === 0) return undefined;
  const current = nested.current_step ?? props.current_step;
  return {
    steps,
    currentStep: typeof current === "string" ? current : undefined,
  };
}

export default function CanvasStaticStepper({
  config,
  title,
}: {
  config: StaticStepperConfig;
  title?: string;
}) {
  const currentIndex = config.steps.findIndex(
    (step) => step.label === config.currentStep,
  );
  return (
    <Sheet
      variant="outlined"
      sx={{ p: 2, borderRadius: "sm", height: "100%", overflowY: "auto" }}
    >
      {title && (
        <Typography level="title-md" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      <Stepper orientation="vertical" aria-label={title || "Progress"}>
        {config.steps.map((step, index) => {
          const state =
            currentIndex < 0 || index > currentIndex
              ? "upcoming"
              : index < currentIndex
                ? "done"
                : "current";
          return (
            <Step
              key={`${index}:${step.label}`}
              completed={state === "done"}
              active={state === "current"}
              disabled={state === "upcoming"}
              data-testid="stepper-step"
              data-step-state={state}
              indicator={
                <StepIndicator
                  variant={state === "upcoming" ? "outlined" : "solid"}
                  color={state === "done" ? "success" : "primary"}
                >
                  {state === "done" ? (
                    <CheckRoundedIcon fontSize="small" />
                  ) : (
                    index + 1
                  )}
                </StepIndicator>
              }
            >
              <Typography
                level="title-sm"
                aria-current={state === "current" ? "step" : undefined}
              >
                {step.label}
              </Typography>
              {step.description && (
                <Typography level="body-xs">{step.description}</Typography>
              )}
            </Step>
          );
        })}
      </Stepper>
    </Sheet>
  );
}
